import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useNavbarOffset } from '../../utils/navbarOffset';

/**
 * Firandebannern — när en offert godkänns ska hela Leadme se att vi sålt ett
 * projekt, oavsett var i portalen man står.
 *
 * Monteras globalt i App.js, inuti <Router> men utanför <Routes>, så att den
 * syns på alla sidor och överlever navigering.
 *
 * Bannern ligger kvar tills var och en stängt sin egen — den som satt i möte
 * när affären gick igenom ska se den vid nästa inloggning, inte missa den. Att
 * stänga är personligt och sparas i databasen (Firande.seddAv), inte i
 * localStorage, så det följer med mellan dator och mobil.
 */

const INTERNA_ROLLER = ['byggkonsult', 'inredningsarkitekt', 'admin'];

/**
 * Sidor som inte kräver inloggning — där tiger bannern.
 *
 * En webbläsare kan ha en levande session kvar i localStorage och stå på
 * inloggningssidan ändå. Bannern hämtade då sitt firande och skrev ut ett
 * KUNDNAMN på en skärm som ser utloggad ut: på en delad kontorsdator eller en
 * olåst laptop räckte det att öppna portalen för att läsa det, utan lösenord.
 * Behörigheten var aldrig bruten — servern kräver en giltig token och släpper
 * bara in interna och byggare — men uppgifterna hörde inte på den skärmen.
 */
const UTLOGGADE_SIDOR = [
  /^\/$/,
  /^\/login\b/,
  /^\/register\b/,
  /^\/change-password\b/,
  /^\/meeting-response\b/
];

/**
 * Bannern säger olika saker till Leadme och till byggaren. För kollegorna är
 * nyheten att vi sålt ett projekt; för byggaren att de fått jobbet. Samma
 * händelse, två helt olika besked.
 */
const rubrikFor = (firande, arByggare) => {
  const konsult = (firande?.konsultNamn || '').trim();
  const kund = (firande?.kundNamn || '').trim();

  if (arByggare) {
    return kund ? `Ni har fått jobbet hos ${kund}!` : 'Ni har fått jobbet!';
  }

  // Namnen är snapshots och kan saknas på äldre eller halvifyllda rader.
  // Firandet är värt att visa ändå — bara med en text som inte ser trasig ut.
  if (konsult && kund) return `${konsult} har hjälpt ${kund}!`;
  if (kund) return `Vi har hjälpt ${kund}!`;
  if (konsult) return `${konsult} har sålt ett projekt!`;
  return 'Vi har sålt ett projekt!';
};

const underradFor = (firande, arByggare) => {
  const delar = [];
  if (firande?.projektId) delar.push(`Projekt #${firande.projektId}`);

  // Byggaren vet vilket företag de själva är — för dem är byggarnamnet brus.
  if (!arByggare && firande?.builderNamn) delar.push(firande.builderNamn);

  delar.push(firande?.godkandAvKonsult
    ? 'offerten är godkänd åt kund'
    : 'kunden har godkänt offerten');

  // Står beskedet först i meningen ska det ha versal
  if (delar.length === 1) {
    return delar[0].charAt(0).toUpperCase() + delar[0].slice(1);
  }
  return delar.join(' · ');
};

const FirandeBanner = () => {
  const { user } = useAuth();
  const socket = useSocket();
  const [firanden, setFiranden] = useState([]);
  const [stanger, setStanger] = useState(false);

  const { pathname } = useLocation();

  const arByggare = user?.role === 'byggare';
  const arUtloggadSida = UTLOGGADE_SIDOR.some(monster => monster.test(pathname));
  // Byggaren ser bara sina egna jobb — filtreringen görs i backend, inte här
  const farSeFiranden = Boolean(user?._id)
    && (INTERNA_ROLLER.includes(user?.role) || arByggare)
    && !arUtloggadSida;

  // Hämta det jag ännu inte stängt. Kundinloggningar frågar aldrig — endpointen
  // släpper bara in interna och byggare, och hade svarat 403.
  useEffect(() => {
    if (!farSeFiranden) {
      setFiranden([]);
      return;
    }

    let avbruten = false;
    (async () => {
      try {
        const res = await api.get('/api/firanden/aktuella');
        if (!avbruten) setFiranden(res.data?.firanden || []);
      } catch (fel) {
        // Ett uteblivet firande är inget att störa användaren med
        console.warn('Kunde inte hämta firanden:', fel.message);
      }
    })();

    return () => { avbruten = true; };
  }, [farSeFiranden]);

  // Live medan man är inloggad. Servern avgör vem som får eventet: kollegorna
  // via det interna rummet, byggaren bara för sitt eget jobb.
  useEffect(() => {
    if (!socket || !farSeFiranden) return;

    const nyttFirande = (firande) => {
      if (!firande?._id) return;
      setFiranden(prev => (
        prev.some(f => String(f._id) === String(firande._id)) ? prev : [firande, ...prev]
      ));
    };

    socket.on('firande', nyttFirande);
    return () => socket.off('firande', nyttFirande);
  }, [socket, farSeFiranden]);

  const stang = useCallback(async (id) => {
    setStanger(true);
    // Ta bort direkt — bannern ska försvinna när man klickar, inte när nätet
    // svarat. Misslyckas anropet dyker den upp igen vid nästa laddning, vilket
    // är en bättre utgång än en banner som sitter fast.
    setFiranden(prev => prev.filter(f => String(f._id) !== String(id)));
    try {
      await api.post(`/api/firanden/${id}/sedd`);
    } catch (fel) {
      console.warn('Kunde inte markera firande som sett:', fel.message);
    } finally {
      setStanger(false);
    }
  }, []);

  const aktuellt = firanden[0];
  const toppOffset = useNavbarOffset(Boolean(aktuellt));

  if (!aktuellt) return null;

  const kvar = firanden.length - 1;

  return (
    <div
      className="fixed left-1/2 z-[60] w-[calc(100%-2rem)] max-w-2xl animate-slide-down"
      style={{ top: toppOffset + 12, transform: 'translateX(-50%)' }}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3 sm:gap-4 rounded-xl border border-leadme-pink-dark/30 bg-gradient-to-r from-leadme-pink to-leadme-pink-dark px-4 py-3 sm:px-5 sm:py-4 shadow-xl">
        <span className="text-3xl sm:text-4xl leading-none select-none" aria-hidden="true">🎉</span>

        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold text-base sm:text-lg leading-snug break-words">
            {rubrikFor(aktuellt, arByggare)}
          </p>
          <p className="text-white/90 text-xs sm:text-sm mt-0.5 break-words">
            {underradFor(aktuellt, arByggare)}
            {kvar > 0 && (
              <span className="ml-2 inline-block rounded-full bg-white/25 px-2 py-0.5 text-[11px] font-medium">
                +{kvar} till
              </span>
            )}
          </p>
        </div>

        <button
          type="button"
          onClick={() => stang(aktuellt._id)}
          disabled={stanger}
          aria-label="Stäng firandet"
          title="Stäng"
          className="flex-shrink-0 rounded-lg p-1.5 text-white/80 hover:text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70 transition-colors disabled:opacity-50"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default FirandeBanner;
